/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Txn } from '@src/app/model/txn';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { InvoiceService } from '@src/app/modules/txn/invoice.service';
import { Cycle } from '@src/app/model/cycle';
import { Field } from '@src/app/shared/field/Field';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { GridField } from '@src/app/shared/grid/grid-field';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { nonZero, required, validateDate, validateTxnDate } from '@src/app/shared/validators';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import moment from 'moment';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { Unit } from '@src/app/model/unit';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { BCode } from '@src/app/model/bcode';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { Schedule } from '@src/app/model/schedule';
import { FormError } from '@src/app/shared/form/form-error/form-error.component';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { MailInvoicePDFAction } from '@src/app/modules/txn/invoice-page/mail-invoice-pdf-action';
import { MailService } from '@src/app/shared/mail-service';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { Period } from '@src/app/model/period';
import { RegeneratePDFAction } from '@src/app/modules/txn/invoice-page/regenerate-invoice-pdf';
import { EditInvoiceNotesAction } from '@src/app/modules/txn/invoice-page/edit-invoice-notes-action';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { InvoicePageNavRoute } from '@src/app/routes/invoice-page-nav';
import { CreditNotePageNavRoute } from '@src/app/routes/credit-note-page.nav';
import { OmcSettingsService } from '@src/app/pages/omc-settings/omc-settings.service';
import { OmcSettings } from '@src/app/model/OmcSettings';
import { concatMap, first, map } from 'rxjs/operators';
import { VatRateService } from '@src/app/modules/txn/VATRate.service';
import { uuid } from '@src/app/model/abstract-object';
import { MessageService } from '@src/app/shared/message.service';

@Component({
    selector: 'app-invoice-page',
    templateUrl: './invoice-page.component.html',
    styleUrls: ['./invoice-page.component.scss'],
    imports: [FormPageComponent]
})
export class InvoicePageComponent extends AbstractPageComponent {
    dataSvc = inject(InvoiceService);
    bCodeSvc = inject(BCodeService);
    protected activeRoute = inject(ActivatedRoute);
    protected unitSvc = inject(UnitService);
    personSvc = inject(PersonService);
    private msgSvc = inject(MessageService);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);
    protected scheduleSvc = inject(ScheduleService);
    private omcSettingsSvc = inject(OmcSettingsService);
    private vatRateSvc = inject(VatRateService);
    private currentUserSvc = inject(CurrentUserService);
    private mailSvc = inject(MailService);
    dialogRef = inject<MatDialogRef<InvoicePageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    readonly path = Txn.TYPE.INVOICE.code

    bCodes: BCode[] = [];
    lineItemBCodes: BCode[] = [];

    cycles: Cycle[] = [];
    periods: Period[] = [];

    vatRegistered = false;
    vatControlAccountId: uuid;

    //Dunno why it needs that dumb rfreshes periodId
    cycleField: PicklistField = FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle',
        { items: this.cycles, refreshes: ['periodId'] }, { formColumn: 2, visible: Field.noShow }
    );

    periodField: Field = FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod',
        { items: [] },
        {
            formColumn: 2, visible: Field.noShow,
            refresh: (o: Cycle, control: AppPicklistControl) => { 
                if (o) { 
                    control.field.setPicklistItems(o.periods);
                    control.setValue(null); 
                } 
            },
        }
    );

    txnDateField: Field = FormDateComponent.make('Transaction Date', 'txnDate', {
        cellOpts: { width: '2%' }, formColumn: 3,
        validators: [validateTxnDate(this.cycleField, this.periodField)],
        valueChanges: this.dateValueChanges.bind(this)
    });

    childTxn = this.getChildGrid();

    configReady = new ReplaySubject<null>(1);
    currentCycle: Cycle;
    schedules: Schedule[] = [];

    paymentGrid = new GridField({
        field:
            { label: 'Payments Received', value: 'relations', visible: Field.formOnly, formRow: 2, formColumn: 2, sendServer: false },
        rowFactory: () => [
            FormNavLinkComponent.makeTxnLink('Ref'),
            FormDateComponent.make('Date', 'txnDate', { cellOpts: { width: '2%' }, formColumn: 2 }),
            FormNumberComponent.make("Amount", "credit", { format: 'currency', width: 9, formatParms: '1.2-2' }),
            FieldMaker.notes(),
            FieldMaker.rev(),
            FieldMaker.deleteGridRow(),
        ]
    });

    debitField = FormNumberComponent.make("Total", "debit",
        { format: 'currency', width: 9, formatParms: '1.2-2' },
        { formRow: 1, formColumn: 4, readonly: true, validators: [nonZero] }
    );

    outstandingField = FormNumberComponent.make("Outstanding", "outstanding",
        { format: 'currency', width: 9, formatParms: '1.2-2' },
        { formRow: 1, formColumn: 4, readonly: true, disable: true }
    );

    idField = FieldMaker.id();
    refField = FormNumberComponent.make('Reference', 'refNr', { format: 'plain' }, { cellOpts: { heading: 'Ref' }, readonly: true, formColumn: 3 });

    revField = FieldMaker.rev({ visible: Field.showAll, readonly: true, formColumn: 3 });

    unitEditField = FormComboBoxComponent.make('Unit', 'unitId', 'unit',
        {
            service: this.unitSvc, optionDisplayValue: (o: Unit) => Unit.getFullName(o),
            refreshes: ['personId', 'personFullName', 'Address']
        },
        { formColumn: 1, validators: [required], visible: Field.formOnly },
    );

    unitTableField = FormNavLinkComponent.makeDetail('Unit', 'unit.name', 'unit.id', UnitPageNavRoute)
    .override({visible:  Field.noForm});

    mailItemGrid = this.mailSvc.getMailItemGrid(this.dataSvc, 'model');

    attachmentGrid = AttachmentGridComponent.make(this.dataSvc, { readonly: true});

    personFieldLink = FormNavLinkComponent.makeDetail('Contact', 'person.fullName', 'personId', 
        PersonPageNavRoute).override({
            formColumn: 2,
        }
    );
    personShowField = FormTextComponent.make('Contact', 'personFullName', {
        cellOpts: { width: '20%' }, disable: true,
        calculateValue: (o: Txn) => o.person?.fullName,
        refresh: (o: Unit, ctl: AppFormControl) => ctl.setValue(o?.owner?.fullName),
        formColumn: 2, visible: Field.noShow
    });

    vatTotalField = FormNumberComponent.makeCurrency("VAT", "vatAmount",
        { formRow: 1, formColumn: 4, readonly: true, sendServer: false, visible: Field.noShow}
    );

    config = new FormConfig({
        navRoute: InvoicePageNavRoute,
        docPath: 'omc-books/service-charges/invoices',
        title: Txn.TYPE.INVOICE.name,
        help: $localize`Service Charge Billings`,
        readonly: true,
        fieldSet: new FieldSet({
            fields: [

                this.idField,
                this.refField,

                FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),
                FormTextComponent.make('typeId', 'typeId', { visible: Field.noShow }),
                FormTextComponent.make('bCodeId', 'bCodeId', { visible: Field.noShow }),
                FieldMaker.idHolder('billingId'),

                /* txtDateField Validator overwrites period info, need this order! */
                this.cycleField,
                this.periodField,
                this.txnDateField,
                this.revField,
                this.unitEditField,
                this.unitTableField,

                FormDateComponent.make('Services From', 'svcFrom', {
                    cellOpts: { width: '2%' }, formColumn: 1,
                    validators: [validateDate]
                }),
                FormDateComponent.make('Services To', 'svcTo', {
                    cellOpts: { width: '2%' }, formColumn: 1,
                    validators: [validateDate]
                }),
                this.personFieldLink,
                this.personShowField,
                FormTextAreaComponent.make('Address', 'unit.address',
                    {
                        name: 'Address', formColumn: 2, readonly: true, disable: true, sendServer: false, visible: Field.formOnly,
                        refresh: (o: Unit, ctl: AppFormControl) => ctl.setValue(o?.address),
                    }),

                this.debitField,
                this.vatTotalField,
                this.outstandingField,
                FormTextAreaComponent.make('Notes', 'notes', {
                        name: 'notes', formColumn: 5, visible: Field.formOnly,
                        hint: $localize`Optional notes for this charge that appear on statement and charge`,
                }),

                //FormNumberComponent.make("Paid To Date", "debit", { name: 'debit2', formRow: 1, formColumn: 4, readonly: true, numberOpts: { format: 'currency', width: 9, formatParms: '1.2-2' } }),

                FormNumberComponent.make("Pct Owed", "outstandingPct",
                    { format: 'percent', width: 9, formatParms: '1.2-2' },
                    {
                        formRow: 1, formColumn: 5, readonly: true, disable: true,
                        calculateValue: (o: Txn) => (o.debit ? o.outstanding / o.debit : 0),
                        sendServer: false,
                    }),
                this.childTxn,
                this.paymentGrid,
            ],
            formLayout: [
                { cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, {width:'20%'}] },
                { cells: [{ colspan: 3, pageTab: 'yes' }, { colspan: 2, pageTab: 'yes' }] }
            ],
            formValidator: this.validateInvoice.bind(this),
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newFactory.bind(this),
        configReady: this.configReady,
        beforeEdit: this.beforeEdit.bind(this),
        actions: [new EditInvoiceNotesAction(), new MailInvoicePDFAction(), new RegeneratePDFAction()],
        newOptions: [
            { name: $localize`New ${Txn.TYPE.INVOICE.name}`, basePath: InvoicePageNavRoute.newUrl, id: 'newInvoice' },
            { name: $localize`New ${Txn.TYPE.CREDIT_NOTE.name}`, basePath: CreditNotePageNavRoute.newUrl, id: 'newCreditNote' }
        ],
        tabFields: [this.attachmentGrid, this.mailItemGrid]
    });

    constructor() {

        super();
        const vatRateSvc = this.vatRateSvc;
        const currentUserSvc = this.currentUserSvc;

        // Mysteriously, without the .pipe(first())s the currentUser & defaultBCodes not firing and forkJoin never completes
        forkJoin({currentUser: currentUserSvc.getCurrentUser().pipe(first()),
            schedules: this.scheduleSvc.get(true),
            cycles: this.cycleSvc.get<Cycle>(true),
            bcodes: this.bCodeSvc.get(false),
            defBCodes: this.currentUserSvc.getDefaultBCodes().pipe(first()),
            periods: this.periodSvc.get<Period>(),
            omcSettings: this.omcSettingsSvc.getOne<OmcSettings>('omcSettings', null).pipe( concatMap(
                omcSettings => {
                    if (vatRateSvc.vatRegistered(omcSettings)) {
                        return this.vatRateSvc.getVatRates().pipe( map( () => omcSettings))
                    }
                    return of(omcSettings)
                }
            )),            
        }).subscribe(
            (result) => {
                this.currentUser = result.currentUser;
                this.cycles = result.cycles;
                this.cycleField.setPicklistItems(result.cycles);
                this.currentCycle = this.cycleSvc.getCurrentCycle(result.cycles);
                this.periods = result.periods;
                this.schedules = result.schedules as Schedule[];
                this.bCodes = result.bcodes as BCode[];
                this.lineItemBCodes.length = 0;
                this.bCodes.forEach( bc => {
                    if (bc.typeId === BCode.TYPE.INCOME.id || bc.typeId === BCode.TYPE.CAPITAL.id) {
                        if (!result.defBCodes.find( dbc => dbc.bCodeId === bc.id)) {
                            this.lineItemBCodes.push(bc);
                        }
                    }
                })
                if (this.vatRateSvc.vatRegistered(result.omcSettings)) {
                    this.vatRegistered = true;
                    this.vatTotalField.visible = Field.formOnly;
                    this.vatControlAccountId = this.currentUserSvc.getDefaultBCode('VatControl')?.bCodeId;
                    if (!this.bCodes.find( o => o.id === this.vatControlAccountId))                     {
                        this.msgSvc.showErrorMessage($localize `Vat Control Account is not initialized - cannot continue`);
                    }
                } else {
                    this.vatTotalField.visible = Field.noShow;
                }
                this.configReady.next(null);
            });
    }

    childRowFactory() {
        const bCodeField = FormPicklistComponent.make('Account', 'bCodeId', 'bCode',
            { items: this.lineItemBCodes, refreshes: [this.invoiceValueChanges.bind(this)] },
            { cellOpts: { width: '25%' }, validators: [required] }
        );
        const vatFields = [];
        if (this.vatRegistered) {
            vatFields.push(FormTextComponent.make('VAT Code', 'vatCode', {readonly: true}))
            vatFields.push(FormNumberComponent.make('VAT Rate', 'vatRate', {format: 'decimal', formatParms: '2.2-2'}, {readonly: true}))
            vatFields.push(FormNumberComponent.makeCurrency('VAT', 'vatAmount', {readonly: true}))
        }        
        return [
            FieldMaker.id(),
            FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),
            FormTextComponent.make('typeId', 'typeId', { visible: Field.noShow }),
            FormTextComponent.make('txnDate', 'txnDate', { visible: Field.noShow }),
            FormTextComponent.make('txnCycleId', 'txnCycleId', { visible: Field.noShow }),
            FormTextComponent.make('txnPeriodId', 'txnPeriodId', { visible: Field.noShow }),

            FormPicklistComponent.make('Schedule', 'scheduleId', 'schedule',
                {
                    items: this.schedules, refreshes: [(schedule: Schedule) => {
                        console.log('Value ', schedule);
                        //bCodeField.setValue(schedule.incomeBCode);
                        bCodeField.control.setValue(schedule.incomeBCodeId);
                        console.log({ bCodeField, schedule, val: bCodeField.control.value })
                    }]
                },
            ),
            bCodeField,
            FormNumberComponent.make("Amount", "credit",
                { format: 'currency', width: 9, formatParms: '1.2-2' },
                { valueChanges: this.invoiceValueChanges.bind(this) }
            ),
            ... vatFields,
            FieldMaker.notes({ cellOpts: { width: '50%' }, hint: $localize`Optional notes for this charge that appear on charge line item NOT on statement` }),
            FieldMaker.rev(),
            FieldMaker.deleteGridRow(),
        ];
    }

    getChildGrid() {

        return new GridField({
            field:
                { label: 'Charge Details', value: 'details', visible: Field.formOnly, formRow: 2 },
            rowFactory: this.childRowFactory.bind(this),
            objFactory: this.newChildTxn.bind(this),
        });
    }

    validateInvoice() {

        if (this.outstandingField.control && this.outstandingField.control.value < 0) {
            return FormError.reportError('outstandingSubZero',
                $localize`You cannot change a service charge such that outstanding value is less than zero`);
        }
        return null;
    }
    /*
    * Readonly for displaying, allow entry only
    */
    beforeEdit(o: Txn) {
        this.config.readonly = true; // Need to properly test editting, especially VAT.
        //this.config.readonly = true;
        this.personShowField.visible = Field.noShow;
        this.personFieldLink.visible = Field.showAll;
        this.config.fieldSet.formLayout = [
            { cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }] },
            { cells: [{ colspan: 3, pageTab: 'yes' }, { colspan: 2, pageTab: 'yes' }] }
        ];
        this.paymentGrid.visible = Field.formOnly;

        this.revField.visible = Field.showAll;
        this.refField.visible = Field.showAll;

        if (this.vatRegistered) {
            const vatTxn = o.details.find( t => t.typeId === Txn.TYPE.CHARGES_VAT.id);
            // do not show VAT Line In Grid
            o.details = o.details.filter( t => t.typeId !== Txn.TYPE.CHARGES_VAT.id);
            if (vatTxn) {
                o.vatAmount = vatTxn.credit - vatTxn.debit;
            } else {
                console.log('No VAT Control');
            }
        }
        return o;
    }

    newFactory(): Observable<Txn> {
        this.config.fieldSet.formLayout = [
            { cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }] },
            { cells: [{ colspan: 5, pageTab: 'yes' }] }
        ];
        this.paymentGrid.visible = Field.noShow;
        this.refField.visible = Field.noShow;
        this.revField.visible = Field.noShow;

        this.personShowField.visible = Field.formOnly;
        this.personFieldLink.visible = Field.noForm;        

        const txn = new Txn();
        this.config.readonly = false;

        txn.ledgerId = Txn.LEDGER.AR.id;
        txn.typeId = Txn.TYPE.INVOICE.id;

        txn.bCodeId = this.currentUserSvc.getDefaultBCode('Debtors').bCodeId;
        //txn.bCode = this.currentUser.currentTeam.debtorsBCode;

        // Let user enter it, do not default today
        //txn.txnDate = moment().toISOString().substring(0, 10);
        //txn.txnCycleId = this.cycleSvc.getCurrentCycle().id;
        //txn.txnPeriodId = this.cycleSvc.getCurrentPeriod().id;
        txn.svcFrom = this.cycleSvc.getCurrentCycle(this.cycles).from;
        txn.svcTo = this.cycleSvc.getCurrentCycle(this.cycles).to;

        for (const s of this.schedules) {
            const c = this.createChildItem();
            c.txnDate = moment().toISOString().substring(0, 10);
            const cycle = this.cycleSvc.getCurrentCycle(this.cycles)
            c.txnCycleId = cycle.id;
            c.txnPeriodId = this.cycleSvc.getCurrentPeriod(cycle).id;
            c.bCodeId = s.incomeBCodeId;
            c.scheduleId = s.id;
            c.schedule = s;
            txn.details.push(c);
        }
        // txn.led
        // txn.typeId = Txn.TYPE.INVOICE.id;
        // txn.bCodeId = Defaulted on the server?
        return of(txn);
    }

    createChildItem(): Txn {
        const txn = new Txn();
        txn.ledgerId = Txn.LEDGER.AR.id;
        txn.typeId = Txn.TYPE.INVOICE_ITEM.id;
        txn.txnDate = this.txnDateField.control.value;
        txn.txnPeriodId = this.periodField.control.value;
        txn.txnCycleId = this.cycleField.control.value;
        if (this.schedules.length === 1) {
            txn.scheduleId = this.schedules[0].id;
        }
        return txn;
    }

    newChildTxn(): Observable<Txn> {

        return of(this.createChildItem());
    }

    dateValueChanges(): void {
        const allocations = (this.childTxn.control as GridControl).controls;
        if (allocations) {
            for (const txnRow of allocations) {
                txnRow.get('txnDate').setValue(this.txnDateField.control.value, { emitEvent: false });
                txnRow.get('txnCycleId').setValue(this.cycleField.control.value, { emitEvent: false });
                txnRow.get('txnPeriodId').setValue(this.periodField.control.value, { emitEvent: false });
            }
        }
        this.invoiceValueChanges();
    }

    invoiceValueChanges(): void {
        const txns = (this.childTxn.control as GridControl).gridRows();
        let total = 0;
        const txnDate = this.txnDateField.getFormValue();
        let vatTotal = 0;

        if (txns) {
            for (const tRow of txns) {
                const amt = tRow.get('credit').value;
                const typeId = tRow.get('typeId').value;
                if (this.vatRegistered && typeId !== Txn.TYPE.CHARGES_VAT.id) {

                    const acctId = ((tRow.get('bCodeId') as AppFormControl).value);
                    const bc = this.bCodes.find( ec => ec.id === acctId);

                    tRow.get('vatCode').setValue(bc.vatCode, {emitEvent: false});

                    if (txnDate) {
                        const rate = this.vatRateSvc.getRate(bc.vatCode, txnDate);
                        const vatAmount = Math.round(amt * rate) / 100

                    
                        tRow.get('vatRate').setValue(rate, {emitEvent: false});
                        tRow.get('vatAmount').setValue(vatAmount);

                        total += vatAmount;
                        vatTotal += vatAmount;                    
                    }
                }
                total += amt;
            }
        }
        const pays = this.paymentGrid.control.gridRows();
        let paid = 0;
        if (pays) {
            for (const pay of pays) {
                paid += pay.get('credit').value;
            }
        }

        this.debitField.control.setValue(total);
        this.outstandingField.control.setValue(total - paid);
        this.vatTotalField.control.setValue(vatTotal);

    }

}
